import React from 'react'
import { useEffect, useMemo, useState, FC, MouseEvent, CSSProperties } from 'react'
import EventCell from '../EventCell'
import TimeMark from '../TimeMark'
import styles from './style.module.scss'
import { t } from 'i18next'
import classNames from 'classnames'
import settings from 'popups/Settings'
import { initialState } from 'redux/settings/types'
import { EventEmitter } from 'events'

const SLOT_HEIGHT = 34

const DAY_CHANGE_EMITTER = new EventEmitter()
DAY_CHANGE_EMITTER.setMaxListeners(15)

interface Attachment {
  id: string
}

interface TimelineEvent {
  id: string
  subject: string | null
  startDate: Date
  endDate: Date
  color: string
  folderColor: string
  isBusy: boolean
  isCanceled: boolean
  isDeclined: boolean
  isMeeting: boolean
  myMeetingResponse: string | null
  isMyEvent: boolean
  location?: string
  isPrivate?: boolean
  isRecurring?: boolean
  hasException?: boolean
  attachments?: Attachment[]
}

interface BusyBlock {
  start: Date
  end: Date
}

interface WorkHourDay {
  enabled: boolean
  start: number
  end: number
}

interface TimeSlot {
  id: string
  isWorkHour: boolean
  hasSeparator: boolean
}

interface EventCellData {
  instanceId: string
  title: string
  color: string
  folderColor: string
  isFree: boolean
  isCanceled: boolean
  isDeclined: boolean
  isNotResponse: boolean
  isMyEvent: boolean
  date: Date
  location?: string
  startDate: Date
  endDate: Date
  isPrivate?: boolean
  isRecurring?: boolean
  hasException?: boolean
  hasAttachment: boolean
  duration: number
  backgroundColor: string
  style: CSSProperties
  key: string
  strechable: {
    top: boolean
    bottom: boolean
    left: boolean
    right: boolean
  }
}

interface TimeLineViewProps {
  timeline: TimelineEvent[]
  date: Date
  mode?: 'normal' | 'schedulingAssistant'
  busyBlocks?: BusyBlock[]
  onEventClick?: ((event: EventCellData, cellElement: HTMLDivElement | null) => void) | null
  showTimeMark?: boolean
  onStrechStart?:
    | ((event: EventCellData, isTop: boolean, isLeft: boolean, e: MouseEvent) => void)
    | null
  onStrechMove?:
    | ((event: EventCellData, isTop: boolean, isLeft: boolean, e: globalThis.MouseEvent) => void)
    | null
  onStrechEnd?:
    | ((
        event: EventCellData,
        isTop: boolean,
        isLeft: boolean,
        e: globalThis.MouseEvent | KeyboardEvent,
        cancelled: boolean,
      ) => void)
    | null
  onDoubleClick?: ((e: MouseEvent<HTMLDivElement>, date: Date, isAllday: boolean) => void) | null
  onEventDoubleClick?: ((event: EventCellData, cellElement: HTMLDivElement | null) => void) | null
  readOnly?: boolean
}

const TimeLineView: FC<TimeLineViewProps> = ({
  timeline,
  date,
  mode = 'normal',
  busyBlocks = [],
  onEventClick = null,
  showTimeMark = false,
  onStrechStart = null,
  onStrechMove = null,
  onStrechEnd = null,
  onDoubleClick = null,
  onEventDoubleClick = null,
  readOnly = false,
}) => {
  const HOURS_IN_DAY = 24
  const MINUTES_IN_HOUR = 60
  const [currentTime, setCurrentTime] = useState(new Date())

  const [workHourDay, setWorkHourDay] = useState<WorkHourDay | null>(null)

  const [isToday, setIsToday] = useState(false)
  const calcIsToday = () => {
    const today = new Date()
    const inIsToday =
      today.getFullYear() === date.getFullYear() &&
      today.getMonth() === date.getMonth() &&
      today.getDate() === date.getDate()
    setIsToday(inIsToday)
    return inIsToday
  }

  useEffect(() => {
    const workHours = settings.getValue('calendar').workHours || initialState.calendar.workHours
    setWorkHourDay(workHours[date.getDay()])

    calcIsToday()

    DAY_CHANGE_EMITTER.addListener('DAY_CHANGED', calcIsToday)
    return () => {
      DAY_CHANGE_EMITTER.removeListener('DAY_CHANGED', calcIsToday)
    }
  }, [date])

  useEffect(() => {
    const unsubscribe = settings.getStore().subscribe(() => {
      const workHours = settings.getValue('calendar').workHours || initialState.calendar.workHours
      setWorkHourDay(workHours[date.getDay()])
      console.log('workHours : ', workHours)
    })
    return () => {
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (!showTimeMark || !calcIsToday()) return

    const timer = setInterval(() => {
      const newCurrentDate = new Date()
      if (!calcIsToday()) {
        clearInterval(timer)
        DAY_CHANGE_EMITTER.emit('DAY_CHANGED')
        return
      }
      setCurrentTime(newCurrentDate)
    }, 5000)

    return () => clearInterval(timer)
  }, [showTimeMark, isToday])

  const getCurrentTimePosition = (): number => {
    const now = currentTime
    const minutes = now.getHours() * MINUTES_IN_HOUR + now.getMinutes()
    return (minutes / 30) * SLOT_HEIGHT
  }

  const timelineSlots = useMemo((): TimeSlot[] => {
    const slots: TimeSlot[] = []
    let beforeIsWorkHour = false
    for (let i = 0; i < HOURS_IN_DAY; i++) {
      const hour = i.toString().padStart(2, '0')
      let minutes = i * 60
      const hourSlotId = `${hour}:00`
      let isWorkHour = false
      if (workHourDay?.enabled) {
        if (workHourDay.start > workHourDay.end) {
          isWorkHour = minutes >= workHourDay.start || minutes < workHourDay.end
        } else {
          isWorkHour = minutes >= workHourDay.start && minutes < workHourDay.end
        }
      }
      let hasSeparator = false
      if (isWorkHour !== beforeIsWorkHour) {
        beforeIsWorkHour = isWorkHour
        hasSeparator = i > 0 ? true : false
      }
      slots.push({ id: hourSlotId, isWorkHour, hasSeparator })
      hasSeparator = false

      minutes += 30
      const halfHourSlotId = `${hour}:30`
      let isWorkHalfHour = false
      if (workHourDay?.enabled) {
        if (workHourDay.start > workHourDay.end) {
          isWorkHalfHour = minutes >= workHourDay.start || minutes < workHourDay.end
        } else {
          isWorkHalfHour = minutes >= workHourDay.start && minutes < workHourDay.end
        }
      }
      if (isWorkHalfHour !== beforeIsWorkHour) {
        beforeIsWorkHour = isWorkHalfHour
        hasSeparator = true
      }
      slots.push({ id: halfHourSlotId, isWorkHour: isWorkHalfHour, hasSeparator })
    }
    return slots
  }, [date, workHourDay])

  const eventCells = useMemo((): EventCellData[] => {
    if (!timeline || timeline.length === 0) {
      return []
    }

    const dayStart = new Date(date)
    dayStart.setHours(0, 0, 0, 0)

    const dayEnd = new Date(date)
    dayEnd.setHours(23, 59, 59, 999)

    const overlapGroups: TimelineEvent[][][] = []
    if (timeline.length > 1) {
      let overlapInstances: TimelineEvent[][] = []
      let beforeInstance = timeline[0]

      for (let i = 1; i < timeline.length; i++) {
        const instance = timeline[i]

        let endTime = beforeInstance.endDate.valueOf()
        if (endTime < beforeInstance.startDate.valueOf() + 30 * 60 * 1000) {
          endTime = beforeInstance.startDate.valueOf() + 30 * 60 * 1000
        }

        if (endTime > instance.startDate.valueOf()) {
          const foundIndex = overlapInstances.findIndex((overlapInstance) => {
            return overlapInstance.at(-1)!.endDate.valueOf() <= instance.startDate.valueOf()
          })
          if (foundIndex === -1) {
            if (overlapInstances.length > 0) {
              overlapInstances.push([instance])
            } else {
              overlapInstances = [[beforeInstance], [instance]]
            }
          } else {
            overlapInstances[foundIndex].push(instance)
          }
        } else if (overlapInstances.length > 0) {
          overlapGroups.push(overlapInstances)
          overlapInstances = []
        }

        let newEndTime = instance.endDate.valueOf()
        const minEndTime = instance.startDate.valueOf() + 30 * 60 * 1000
        if (newEndTime < minEndTime) {
          newEndTime = minEndTime
        }
        if (beforeInstance.endDate.valueOf() < newEndTime) {
          beforeInstance = {
            ...instance,
            endDate: new Date(newEndTime),
          }
        }
      }

      if (overlapInstances.length > 0) {
        overlapGroups.push(overlapInstances)
      }
    }

    return timeline.map((event): EventCellData => {
      const containStart = event.startDate < dayStart
      const startDate = containStart ? dayStart : event.startDate

      const containEnd = event.endDate > dayEnd
      const endDate = containEnd ? dayEnd : event.endDate
      const strechableEnd = event.endDate.getTime() - 1 > dayEnd.getTime()

      const startMinutes = startDate.getHours() * MINUTES_IN_HOUR + startDate.getMinutes()
      const endMinutes = endDate.getHours() * MINUTES_IN_HOUR + endDate.getMinutes()
      const tempDurationMinutes = endMinutes - startMinutes
      const durationMinutes = tempDurationMinutes < 30 ? 30 : tempDurationMinutes

      const startSlot = startMinutes / 30
      const slotCount = durationMinutes / 30

      const top = Math.floor(startSlot * SLOT_HEIGHT) + 1
      const height = Math.floor(slotCount * SLOT_HEIGHT) - 3
      let left = 0.2
      let width = 99.6
      overlapGroups.forEach((overlapGroup) => {
        const foundInstanceIndex = overlapGroup.findIndex((instances) => {
          return instances.findIndex((instance) => instance.id === event.id) !== -1
        })
        if (foundInstanceIndex > -1) {
          let areaCount = 1
          if (overlapGroup.length > foundInstanceIndex + 1) {
            const overlapGroupInstances = overlapGroup.slice(foundInstanceIndex + 1)
            const remainAreaInstanceIndex = overlapGroupInstances.findIndex((instances) => {
              return (
                instances.findIndex((instance) => {
                  const instanceStart = instance.startDate
                  let instanceEnd = instance.endDate
                  const durationMinutes =
                    (instance.endDate.getTime() - instance.startDate.getTime()) / 60 / 1000
                  if (durationMinutes < 30) {
                    instanceEnd = new Date(instance.startDate.getTime() + 30 * 60 * 1000)
                  }
                  if (
                    (startDate <= instanceStart && endDate > instanceStart) ||
                    (startDate < instanceEnd && endDate >= instanceEnd) ||
                    (startDate >= instanceStart && startDate < instanceEnd) ||
                    (endDate >= instanceStart && endDate < instanceEnd)
                  ) {
                    return true
                  }
                  return false
                }) !== -1
              )
            })
            if (remainAreaInstanceIndex > -1) {
              areaCount = remainAreaInstanceIndex + 1
            } else {
              areaCount = overlapGroup.length - foundInstanceIndex
            }
          }

          width = 100 / overlapGroup.length
          left = width * foundInstanceIndex + 0.2
          width = width * areaCount - 0.4
        }
      })

      let subject = event.subject
      if (subject == null || subject.length === 0) {
        subject = t('editor.value.noSubject')
      }

      return {
        instanceId: event.id,
        title: subject,
        color: event.color,
        folderColor: event.folderColor,
        isFree: !event.isBusy,
        isCanceled: event.isCanceled,
        isDeclined: event.isDeclined,
        isNotResponse: event.isMeeting && event.myMeetingResponse === null,
        isMyEvent: event.isMyEvent,
        date: new Date(date),
        location: event.location,
        startDate: event.startDate,
        endDate: event.endDate,
        isPrivate: event.isPrivate,
        isRecurring: event.isRecurring,
        hasException: event.hasException,
        hasAttachment: (event.attachments?.length || 0) > 0,
        duration: slotCount * 30,
        backgroundColor: `${event.color}56`,
        style: {
          position: 'absolute',
          top: `${top}px`,
          height: `${height}px`,
          left: `${left}%`,
          width: `${width}%`,
        },
        key: `timeline-${event.id}`,
        strechable: {
          top: !containStart,
          bottom: !strechableEnd,
          left: false,
          right: false,
        },
      }
    })
  }, [timeline, date])

  const handleDoubleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (onDoubleClick) {
      const id = (e.target as HTMLDivElement).id
      if (id == '') return
      const slotPosition = parseInt(id.split('-')[1])
      const minute = slotPosition * 30
      const newDate = new Date(date)
      newDate.setHours(0, 0, 0, 0)
      newDate.setMinutes(minute)

      onDoubleClick(e, newDate, false)
    }
  }

  const renderBusyBlocks = () => {
    if (mode !== 'schedulingAssistant' || !busyBlocks || busyBlocks.length === 0) {
      return null
    }

    return busyBlocks.map((block, index) => {
      const startMinutes = block.start.getHours() * MINUTES_IN_HOUR + block.start.getMinutes()
      const endMinutes = block.end.getHours() * MINUTES_IN_HOUR + block.end.getMinutes()
      const durationMinutes = endMinutes - startMinutes

      const startSlot = startMinutes / 30
      const slotCount = durationMinutes / 30

      const top = Math.floor(startSlot * SLOT_HEIGHT) + 1
      const height = Math.floor(slotCount * SLOT_HEIGHT) - 3

      return (
        <div
          key={`busy-block-${index}`}
          className={styles.busyBlock}
          style={{
            position: 'absolute',
            top: `${top}px`,
            height: `${height}px`,
            left: 0,
            width: '100%',
          }}
        />
      )
    })
  }

  return (
    <div className={styles.timelineView} onDoubleClick={handleDoubleClick}>
      {timelineSlots.map((time, index) => {
        const key = `timeline-${index}`
        return (
          <div
            key={key}
            id={key}
            className={classNames(
              time.id.endsWith(':00') ? styles.timelineRow : styles.timelineHalfRow,
              time.isWorkHour ? null : styles.nonWorkHour,
              time.hasSeparator ? styles.separator : null,
            )}
          />
        )
      })}

      {renderBusyBlocks()}

      {eventCells.map((event) => (
        <EventCell
          {...({
            key: event.key,
            forDay: true,
            event: event,
            onCellClick: onEventClick,
            onCellDoubleClick: onEventDoubleClick,
            onStrechStart: onStrechStart,
            onStrechMove: onStrechMove,
            onStrechEnd: onStrechEnd,
            readOnly: readOnly,
          } as any)}
        />
      ))}

      {showTimeMark && isToday && (
        <TimeMark
          forWeek={true}
          currentTime={currentTime}
          getCurrentTimePosition={getCurrentTimePosition}
        />
      )}
    </div>
  )
}

export default TimeLineView
